// src/components/Comptable/notifications/NotificationsPage.js
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    Chip,
    Button,
    IconButton,
    Divider,
    CircularProgress,
    Alert,
    Badge,
    Grid,
    Card,
    CardContent,
    Stack
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    CheckCircle,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    MarkEmailRead as ReadIcon,
    Refresh as RefreshIcon,
    Circle as UnreadIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import io from 'socket.io-client';
import useNotifySound from "../../../hooks/comptable/useNotifySound";

const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_SERVER_URL || 'http://localhost:3001';

const NotificationsPage = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [socket, setSocket] = useState(null);
    const playNotify = useNotifySound();

    const getIconByType = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircle color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            case 'warning':
                return <WarningIcon color="warning" />;
            case 'info':
            default:
                return <InfoIcon color="info" />;
        }
    };

    const getColorByType = (type) => {
        switch (type) {
            case 'success': return '#4caf50';
            case 'error': return '#f44336';
            case 'warning': return '#ff9800';
            case 'info':
            default: return '#2196f3';
        }
    };

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) throw new Error('Token non trouvé');

            const response = await axios.get('/api/comptable/notifications?limit=100', {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Transformer les données de l'API pour utiliser id au lieu de _id
            const transformedItems = (response.data?.items || []).map(item => ({
                ...item,
                id: item._id || item.id
            }));

            setNotifications(transformedItems);
            setError(null);
        } catch (err) {
            console.error('Erreur lors du chargement des notifications:', err);
            setError('Impossible de charger les notifications');
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            // Utiliser l'endpoint correct de votre API
            await axios.post(`/api/comptable/notifications/${notificationId}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setNotifications(prev =>
                prev.map(notif =>
                    notif.id === notificationId ? { 
                        ...notif, 
                        read: true,
                        readAt: new Date().toISOString()
                    } : notif
                )
            );
        } catch (err) {
            console.error('Erreur lors du marquage comme lu:', err);
        }
    };

    const markAllAsRead = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            // Utiliser l'endpoint correct de votre API
            await axios.post('/api/comptable/notifications/read-all', {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setNotifications(prev =>
                prev.map(notif => ({ 
                    ...notif, 
                    read: true,
                    readAt: new Date().toISOString()
                }))
            );
        } catch (err) {
            console.error('Erreur lors du marquage de tous comme lus:', err);
        }
    };

    const setupWebSocket = () => {
        const token = localStorage.getItem('token');
        if (!token || socket) return;

        try {
            const newSocket = io(SOCKET_SERVER_URL, {
                withCredentials: true,
                transports: ['websocket', 'polling'],
            });

            newSocket.on('connect', () => {
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    if (payload?.userId) {
                        newSocket.emit('join_room', String(payload.userId));
                    }
                } catch (e) {
                    console.warn('Erreur join_room:', e);
                }
            });

            newSocket.on('tej:sync:done', (notification) => {
                playNotify();
                
                // Ajouter la nouvelle notification en haut de la liste
                setNotifications(prev => [
                    {
                        ...notification,
                        id: notification._id || Date.now().toString(), // Utiliser _id de l'API
                        createdAt: new Date().toISOString(),
                        read: false
                    },
                    ...prev
                ]);
            });

            newSocket.on('disconnect', () => {
                console.log('[NotificationsPage] Déconnecté du WebSocket');
            });

            setSocket(newSocket);
        } catch (error) {
            console.error('Erreur WebSocket:', error);
        }
    };

    useEffect(() => {
        fetchNotifications();
        setupWebSocket();

        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, []);

    const unreadCount = notifications.filter(n => !n.readAt).length;

    // Déterminer si une notification est lue
    const isNotificationRead = (notification) => {
        return notification.read || notification.readAt;
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Typography variant="h4" gutterBottom>
                            <NotificationsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                            Notifications
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Gérez vos alertes et notifications système
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={fetchNotifications}
                                disabled={loading}
                            >
                                Actualiser
                            </Button>
                           
                        </Stack>
                    </Grid>
                </Grid>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {unreadCount > 0 ? (
                                    <Badge badgeContent={unreadCount} color="error">
                                        Toutes les notifications
                                    </Badge>
                                ) : (
                                    'Toutes les notifications'
                                )}
                            </Typography>
                            <Chip
                                label={`${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`}
                                color={unreadCount > 0 ? "error" : "default"}
                                size="small"
                            />
                        </Box>

                        {notifications.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <NotificationsIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    Aucune notification
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Vous serez notifié ici des événements importants.
                                </Typography>
                            </Paper>
                        ) : (
                            <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                                {notifications.map((notification, index) => {
                                    const isRead = isNotificationRead(notification);
                                    return (
                                        <React.Fragment key={notification.id}>
                                            <ListItem
                                                disablePadding
                                                sx={{
                                                    bgcolor: isRead ? 'transparent' : 'action.hover',
                                                    borderRadius: 1,
                                                    mb: 1
                                                }}
                                            >
                                                <ListItemButton
                                                    sx={{ py: 2 }}
                                                    onClick={() => !isRead && markAsRead(notification.id)}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 48 }}>
                                                        {getIconByType(notification.type)}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        fontWeight: isRead ? 400 : 600,
                                                                        color: getColorByType(notification.type)
                                                                    }}
                                                                >
                                                                    {notification.title}
                                                                </Typography>
                                                                {!isRead && (
                                                                    <UnreadIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                                                )}
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <>
                                                                <Typography variant="body2" color="text.primary">
                                                                    {notification.message}
                                                                </Typography>
                                                                {notification.metadata && (
                                                                    <Box sx={{ mt: 1 }}>
                                                                        {Object.entries(notification.metadata).map(([key, value]) => (
                                                                            <Chip
                                                                                key={key}
                                                                                label={`${key}: ${value}`}
                                                                                size="small"
                                                                                sx={{ mr: 0.5, mt: 0.5 }}
                                                                                variant="outlined"
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ display: 'block', mt: 1 }}
                                                                >
                                                                    {format(new Date(notification.createdAt), "PPpp", { locale: fr })}
                                                                </Typography>
                                                                {notification.readAt && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ display: 'block', mt: 0.5 }}
                                                                    >
                                                                        Lu le {format(new Date(notification.readAt), "PPpp", { locale: fr })}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        }
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                            {index < notifications.length - 1 && <Divider variant="inset" component="li" />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </CardContent>
                </Card>

                <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
                    <Typography variant="h6" gutterBottom>
                        À propos des notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Les notifications vous informent en temps réel des événements importants dans votre application :
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle color="success" fontSize="small" />
                                <Typography variant="body2">Succès - Actions terminées</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ErrorIcon color="error" fontSize="small" />
                                <Typography variant="body2">Erreurs - Problèmes détectés</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningIcon color="warning" fontSize="small" />
                                <Typography variant="body2">Avertissements - À surveiller</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InfoIcon color="info" fontSize="small" />
                                <Typography variant="body2">Informations - Événements divers</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </Container>
    );
};

export default NotificationsPage;